<template>
  <div>
    <v-navigation-drawer
      v-model="panel.activated"
      :width="$isMobile ? '100%' : '60%'"
      right
      fixed
      :permanent="panel.activated"
      hide-overlay
      class="z-index--100">
      <div class="ma-3">
        <div class="d-flex justify-space-between">
          <div class="d-flex">
            <v-btn
              dark
              fab
              x-small
              outlined
              color="primary"
              class="mr-3"
              @click="closeDialog"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <div
              class="subtitle-1 primary--text mr-3">
              Devolución: {{ detail.uuid }}
            </div>
          </div>
          <div>
            <v-tooltip
            v-if="isUserProvider"
            left>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  fab
                  color="primary"
                  x-small
                  depressed
                  @click="printOrder"
                >
                  <v-icon
                    small
                  >
                    mdi-printer
                  </v-icon>
                </v-btn>
              </template>
              <span>Imprimir orden de compra</span>
            </v-tooltip>
            <v-tooltip
            v-if="purchaseOrderPermissions['send-purchase-order-via-email']"
            left>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  fab
                  color="primary"
                  x-small
                  depressed
                  @click="openSendByEmailDialog"
                >
                  <v-icon
                    small
                  >
                    mdi-email
                  </v-icon>
                </v-btn>
              </template>
              <span>Enviar orden de compra por mail</span>
            </v-tooltip>
            <v-tooltip
              v-if="purchaseOrderPermissions['copy-purchase-order-link']"
              left>
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  fab
                  color="primary"
                  x-small
                  depressed
                  v-clipboard:copy="copyToLink"
                  v-clipboard:success="onCopy"
                >
                  <v-icon
                    small
                  >
                    mdi-link-variant
                  </v-icon>
                </v-btn>
              </template>
              <span>Copy link</span>
            </v-tooltip>
          </div>
        </div>

        <v-container ref="order-detail">
          <v-row>
            <v-col md="12">
              <v-card outlined>
                <v-row>
                  <v-col md="6" cols="12">
                    <div class="subtitle-2 mb-2 px-2">
                      <div v-if="$is() === 'provider'">
                        <div>
                          Restaurante:
                        </div>
                        <div class="grey--text">
                          {{ order.restaurant }}
                        </div>
                      </div>
                      <div v-else>
                        <div>
                          Proveedor:
                        </div>
                        <div class="grey--text">
                          {{ detail.provider ? detail.provider.name : '' }}
                        </div>
                      </div>
                    </div>
                    <div class="subtitle-2 mb-2 px-2">
                      <div>
                        Nombre del emisor:
                      </div>
                      <div class="grey--text">
                        {{ detail.author && detail.author.email ? detail.author.email : '' }}
                      </div>
                    </div>
                    <div class="subtitle-2 mb-2 px-2">
                      <div>
                        Estatus:
                      </div>
                      <div :class="`${statusColors(detail.status)}--text`">
                        {{ statusNames(detail.status) }}
                      </div>
                    </div>
                  </v-col>
                  <v-col md="3" cols="12">
                    <div class="subtitle-2 mb-2 px-2">
                      <div>
                        Fecha de emisión:
                      </div>
                      <div class="grey--text">
                        {{ convertToDateString(detail.createdAt) }}
                      </div>
                    </div>
                    <div
                      v-if="$is() !== 'provider'"
                      class="subtitle-2 mb-2 pa-2">
                      <div>
                        Total de movimiento(con impuestos):
                      </div>
                      <div class="grey--text">
                        ${{ total | toFix2 | abs | numberWithComma }}
                      </div>
                    </div>
                  </v-col>
                </v-row>
                <div v-if="$is() === 'provider'" class="d-flex justify-space-between px-2 subtitle-2">
                  <span
                    class="font-weight-bold mb-2">
                    Subtotal:
                  </span>
                  <span class="title">${{ subtotal | toFix2 | abs | numberWithComma }}</span>
                </div>
                <div v-if="$is() === 'provider'" class="d-flex justify-space-between px-2 subtitle-2">
                  <span
                    class="font-weight-bold mb-2">
                    IVA:
                  </span>
                  <span class="title">${{ iva | toFix2 | numberWithComma }}</span>
                </div>
                <div v-if="$is() === 'provider'" class="d-flex justify-space-between px-2 subtitle-2">
                  <span
                    class="font-weight-bold mb-2">
                    IEPS:
                  </span>
                  <span class="title">${{ ieps | toFix2 | numberWithComma }}</span>
                </div>
                <div class="d-flex justify-space-between px-2 subtitle-2">
                  <span class="font-weight-bold mb-2">
                    Total:
                  </span>
                  <span class="title">${{ totalToPay | toFix2 | abs | numberWithComma }}</span>
                </div>
              </v-card>
            </v-col>

            <v-col md="12">
              <div>
                <v-data-table
                  :loading-text="table.title"
                  :server-items-length="table.total"
                  :loading="table.loading"
                  :headers="getHeaders"
                  :items="table.data"
                  :options="table.options"
                  sort-by="product"
                  class="elevation-1"
                  hide-default-footer>
                  <template
                    v-slot:item.amount="{ item }"
                    v-if="['sent', 'confirmed'].includes(detail.status) && $is() === 'provider'">
                    <v-edit-dialog
                      :return-value.sync="item.amount"
                      large
                      dark
                      persistent
                      @save="save(item)"
                      cancel-text="Cancelar"
                      save-text="Guardar">
                      ${{ (item.amount || 0) | toFixNumber(2) }}
                      <v-icon light small>mdi-border-color</v-icon>
                      <template v-slot:input>
                        <v-text-field
                          v-model.number="item.amount"
                          label="Edit"
                          single-line>
                        </v-text-field>
                      </template>
                    </v-edit-dialog>
                  </template>
                  <template v-else v-slot:item.amount="{ item }">
                    <div class="text-right">
                      ${{ (item.amount || 0) | toFixNumber(2) | abs | numberWithComma }}
                    </div>
                  </template>
                  <template v-slot:item.unit.name="{ item }">
                    <div>
                      {{ item.unit.name }}
                    </div>
                    <div v-if="item.unit._id !== item.contentUnit" class="grey--text">
                      de {{ item.contentQuantity }} {{ getUnitName(item.contentUnit) }} c/u
                    </div>
                  </template>
                  <template v-slot:item.amountWithTax="{ item }">
                    <div class="text-right">
                      ${{ (item.amountWithTax || 0) | toFixNumber(2) | abs | numberWithComma }}
                    </div>
                  </template>
                  <template v-slot:item.quantityConfirmed="{ item }">
                    <v-edit-dialog
                      v-if="['sent', 'confirmed'].includes(detail.status) && $is() === 'provider' || ['sent'].includes(detail.status) && $is() !== 'provider'"
                      :return-value.sync="item.quantityConfirmed"
                      large
                      dark
                      persistent
                      @save="save(item)"
                      cancel-text="Cancelar"
                      save-text="Guardar">
                      {{ item.quantityConfirmed }}
                      <v-icon light small>mdi-border-color</v-icon>
                      <template v-slot:input>
                        <v-text-field
                          v-model.number="item.quantityConfirmed"
                          label="Edit"
                          type="number"
                          single-line>
                        </v-text-field>
                      </template>
                    </v-edit-dialog>
                    <template v-else>
                      <div class="text-right">
                        <span v-bind:class="{'red--text': item.quantityRequested !== item.quantityConfirmed}">
                          {{ item.quantityConfirmed }}
                        </span>
                      </div>
                    </template>
                  </template>
                  <template
                    v-slot:item.quantityReceived="{ item }"
                    v-if="(detail.status === 'invoiced' || (detail.status === 'confirmed' && detail.provider.receiveWithoutInvoice)) && ['manager', 'stockmanager'].includes($is())">
                    <v-text-field
                      v-model.number="item.quantityReceived"
                      type="number"
                      label="Edit"
                      single-line
                      @input="calculateTotalOnReceive(item)">
                    </v-text-field>
                  </template>
                  <template v-slot:item.totalWithTax="{ item }">
                    ${{ (item.totalWithTax || 0) | toFixNumber(2) | abs }}
                  </template>
                  <template v-slot:item.total="{ item }">
                    ${{ (item.total || 0) | toFixNumber(2) }}
                  </template>
                  <template v-slot:item.actions="{ item }">
                    <v-btn
                      x-small
                      fab
                      depressed
                      text
                      class="mr-2"
                      color="error"
                      @click="openRemoveDialog(item)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </div>
            </v-col>

            <v-col md="12">
              <div  class="subtitle-1 primary--text mr-3">
                Orden de compra que se aplicó devolución: {{ refundDetail.uuid }}
              </div>

              <v-card outlined class="mt-5">
                <div class="subtitle-1 px-2">
                  Facturas:
                </div>
                <div class="pa-2">
                  <ul>
                    <li v-for="(item, index) in refundDetail.invoices" :key="item._id">
                      <v-divider v-if="index > 0"/>
                      <div class="d-flex justify-space-between align-center">
                        <div>
                          <div>
                            <a class="primary--text" href="#" @click.prevent="viewInvoice(item)">{{ item.name }}</a>
                          </div>
                          <div class="caption">
                            Folio: <span class="primary--text">{{ item.folio }}</span>
                          </div>
                          <div :class="[`${invoiceStatuses[item.status].color}--text`, 'caption']">
                            {{ invoiceStatuses[item.status].text }}
                          </div>
                        </div>
                        <!-- <div v-if="item.status === 'active' && purchaseOrderPermissions['change-purchase-order-cancel-invoice']">
                          <v-btn
                            color="error"
                            text
                            small
                            rounded
                            @click="openCancelInvoiceDialog(item)">
                            Cancelar factura
                          </v-btn>
                        </div> -->
                      </div>
                    </li>
                  </ul>
                </div>
              </v-card>

              <v-card outlined class="mt-5">
                <div class="subtitle-1 px-2">
                  Nota de crédito:
                </div>
                <div class="pa-2">
                  <ul>
                    <li v-for="(item, index) in refundDetail.creditNotes" :key="item._id">
                      <v-divider v-if="index > 0"/>
                      <div class="d-flex justify-space-between align-center pa-2">
                        <div>
                          <div>
                            <a class="primary--text" href="#" @click.prevent="viewCreditNote(item)">{{ item.name }}</a>
                          </div>
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              </v-card>
            </v-col>

            <v-col md="12">
              <div>
                <v-data-table
                  :loading-text="refundedTable.title"
                  :server-items-length="refundedTable.total"
                  :loading="refundedTable.loading"
                  :headers="getHeaders"
                  :items="refundedTable.data"
                  :options="refundedTable.options"
                  sort-by="product"
                  class="elevation-1"
                  hide-default-footer>
                  <template
                    v-slot:item.amount="{ item }"
                    v-if="['sent', 'confirmed'].includes(detail.status) && $is() === 'provider'">
                    <v-edit-dialog
                      :return-value.sync="item.amount"
                      large
                      dark
                      persistent
                      @save="save(item)"
                      cancel-text="Cancelar"
                      save-text="Guardar">
                      ${{ (item.amount || 0) | toFixNumber(2) }}
                      <v-icon light small>mdi-border-color</v-icon>
                      <template v-slot:input>
                        <v-text-field
                          v-model.number="item.amount"
                          label="Edit"
                          single-line>
                        </v-text-field>
                      </template>
                    </v-edit-dialog>
                  </template>
                  <template v-else v-slot:item.amount="{ item }">
                    <div class="text-right">
                      ${{ (item.amount || 0) | toFixNumber(2) | numberWithComma }}
                    </div>
                  </template>
                  <template v-slot:item.unit.name="{ item }">
                    <div>
                      {{ item.unit.name }}
                    </div>
                    <div v-if="item.unit._id !== item.contentUnit" class="grey--text">
                      de {{ item.contentQuantity }} {{ getUnitName(item.contentUnit) }} c/u
                    </div>
                  </template>
                  <template v-slot:item.amountWithTax="{ item }">
                    <div class="text-right">
                      ${{ (item.amountWithTax || 0) | toFixNumber(2) | numberWithComma }}
                    </div>
                  </template>
                  <template v-slot:item.quantityConfirmed="{ item }">
                    <v-edit-dialog
                      v-if="['sent', 'confirmed'].includes(detail.status) && $is() === 'provider' || ['sent'].includes(detail.status) && $is() !== 'provider'"
                      :return-value.sync="item.quantityConfirmed"
                      large
                      dark
                      persistent
                      @save="save(item)"
                      cancel-text="Cancelar"
                      save-text="Guardar">
                      {{ item.quantityConfirmed }}
                      <v-icon light small>mdi-border-color</v-icon>
                      <template v-slot:input>
                        <v-text-field
                          v-model.number="item.quantityConfirmed"
                          label="Edit"
                          type="number"
                          single-line>
                        </v-text-field>
                      </template>
                    </v-edit-dialog>
                    <template v-else>
                      <div class="text-right">
                        <span v-bind:class="{'red--text': item.quantityRequested !== item.quantityConfirmed}">
                          {{ item.quantityConfirmed }}
                        </span>
                      </div>
                    </template>
                  </template>
                  <template
                    v-slot:item.quantityReceived="{ item }"
                    v-if="(detail.status === 'invoiced' || (detail.status === 'confirmed' && detail.provider.receiveWithoutInvoice)) && ['manager', 'stockmanager'].includes($is())">
                    <v-text-field
                      v-model.number="item.quantityReceived"
                      type="number"
                      label="Edit"
                      single-line
                      @input="calculateTotalOnReceive(item)">
                    </v-text-field>
                  </template>
                  <template v-slot:item.totalWithTax="{ item }">
                    ${{ (item.totalWithTax || 0) | toFixNumber(2) }}
                  </template>
                  <template v-slot:item.total="{ item }">
                    ${{ (item.total || 0) | toFixNumber(2) }}
                  </template>
                  <template v-slot:item.actions="{ item }">
                    <v-btn
                      x-small
                      fab
                      depressed
                      text
                      class="mr-2"
                      color="error"
                      @click="openRemoveDialog(item)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                </v-data-table>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-navigation-drawer>

    <SendOrderByEmail
      ref="sendOrderByEmail"
      :order="order._id"
    />

    <CreditNoteViewer
      :isOpenDialog.sync="creditNoteViewerOpen"
      :order="refundDetail"
      :file="file"
      :key="`creditNote-${detail.id}`"
      @close="creditNoteViewerOpen = false"/>

    <InvoiceViewer
      ref="invoice"
      :order="refundDetail"
      :file="file"
      :key="`invoice-${detail.id}`"
      @close="invoiceViewerOpen = false"/>

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-overlay :value="overlayPropagate" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>
<script>
import InvoiceViewer from '@/components/modals/InvoiceViewer'
import CreditNoteViewer from '@/components/modals/CreditNoteViewer'
import Order from '@/models/Order'
import SendOrderByEmail from '@/components/modals/SendOrderByEmail'
import print from 'print-js'

export default {
  props: ['dialog', 'order'],
  components: {
    InvoiceViewer,
    CreditNoteViewer,
    SendOrderByEmail
  },
  data () {
    let orderModel = new Order()
    let refundOrder = new Order()

    return {
      invoiceViewerOpen: false,
      creditNoteViewerOpen: false,
      onBoardingProvider: false,
      panel: {
        activated: false
      },
      orderModel,
      refundOrder,
      overlay: false,
      cancelInvoiceDialog: false,
      generateInvoiceDialog: false,
      addProductDialog: false,
      billDialog: false,
      cancelDialog: false,
      refundDialog: false,
      removeDialog: false,
      thanksDialog: false,
      attachDialog: false,
      creditNoteDialog: false,
      forceCancelInvoice: false,
      detail: {},
      refundDetail: {},
      table: {
        title: 'Cargando',
        loading: false,
        data: [],
        total: 0,
        headers: this.getHeaders,
        options: {
          itemsPerPage: 15,
          page: 1
        }
      },
      refundedTable: {
        title: 'Cargando',
        loading: false,
        data: [],
        total: 0,
        headers: this.getHeaders,
        options: {
          itemsPerPage: 15,
          page: 1
        }
      },
      rules: {
        required: [v => !!v || this.$t('validations.required')],
        max: [v => v.length <= 9 || this.$t('validations.max', { count: 9 })]
      },
      file: '',
      itemToRemove: {},
      form: {
        Folio: '',
        CfdiUse: '',
        PaymentForm: '',
        xmlFiles: []
      },
      invoice: {},
      invoiceStatuses: {
        active: {
          text: 'Activa',
          color: 'primary'
        },
        canceled: {
          text: 'Cancelada',
          color: 'danger'
        }
      },
      product: {},
      products: {
        data: [],
        loading: false
      },
      choices: {
        data: [],
        disabled: true,
        loading: false
      },
      validAddProduct: false,
      deliverDialog: false,
      propagateDialog: false,
      deliverForm: {
        nip: null
      },
      agreeDeliver: null,
      showNip: false,
      notes: '',
      isModificated: false
    }
  },
  watch: {
    order: async function (value) {
      if (value && (value._id || (value.$ && value.$.id))) {
        this.orderModel = new Order(value.$ || value)
        this.refundOrder = new Order({ _id: (value.$ || {}).orderRefunded || value.orderRefunded })
        await this.load()
      }
    },
    dialog: function (val) {
      this.panel.activated = val
    }
  },
  computed: {
    overlayPropagate () {
      return this.$store.state.currentProvider.loadingPropagate
    },
    totalProducts () {
      return this.table.data.length
    },
    getHeaders () {
      const _headers = [
        { text: '#', value: 'number', sortable: false },
        { text: 'Insumo', value: 'name', sortable: false }
      ]

      if (['provider', 'provider-buyer'].includes(this.$is())) {
        if (!['sent'].includes(this.detail.status) || !this.detail.isRead) {
          _headers.push(
            { text: 'Cantidad solicitada', value: 'quantityRequested', sortable: false, align: 'end' },
            { text: 'Unidad de medida', value: 'unit.name', sortable: false }
          )
        }
      } else {
        _headers.push(
          { text: 'Cantidad solicitada', value: 'quantityRequested', sortable: false, align: 'end' },
          { text: 'Unidad de medida', value: 'unit.name', sortable: false }
        )
      }

      if (['sent', 'confirmed'].includes(this.detail.status)) {
        _headers.push({ text: 'Cantidad confirmada', value: 'quantityConfirmed', sortable: false })

        if (this.$is() === 'provider') {
          _headers.push({ text: 'Precio unitario (Sin impuestos)', value: 'amount', sortable: false })
          if (this.hasTaxes(this.table.data)) {
            _headers.push({ text: 'IVA', value: 'iva', sortable: false })
          }
          if (this.hasTaxes(this.table.data)) {
            _headers.push({ text: 'IEPS', value: 'ieps', sortable: false })
          }
        } else {
          _headers.push({ text: 'Precio unitario (Con impuestos)', value: 'amountWithTax', sortable: false, align: 'end' })
        }
      } else {
        _headers.push({ text: 'Precio unitario (Con impuestos)', value: 'amountWithTax', sortable: false, align: 'end' })
      }

      if (['invoiced', 'received', 'invalid'].includes(this.detail.status)) {
        _headers.push({ text: 'Cantidad confirmada', value: 'quantityConfirmed', sortable: false, align: 'end' })
      }

      if ((['invoiced'].includes(this.detail.status) || (['confirmed'].includes(this.detail.status) && this.detail.provider.receiveWithoutInvoice)) && ['manager', 'stockmanager'].includes(this.$is())) {
        _headers.push({ text: 'Cantidad recibida', value: 'quantityReceived', sortable: false, align: 'end' })
      }

      if (['received', 'invalid'].includes(this.detail.status)) {
        if (this.$is() === 'provider') {
          _headers.push({ text: 'Cantidad entregada', value: 'quantityReceived', sortable: false, align: 'end' })
        } else {
          _headers.push({ text: 'Cantidad recibida', value: 'quantityReceived', sortable: false, align: 'end' })
        }
      }

      if (['sent', 'confirmed'].includes(this.detail.status)) {
        if (this.$is() === 'provider') {
          _headers.push({ text: 'Total', value: 'total', sortable: false, align: 'end' })
        } else {
          _headers.push({ text: 'Total', value: 'totalWithTax', sortable: false, align: 'end' })
        }
      } else {
        _headers.push({ text: 'Total', value: 'totalWithTax', sortable: false, align: 'end' })
      }

      if (this.detail.status === 'created' && ['manager', 'stockmanager'].includes(this.$is())) {
        _headers.push({ text: 'Acciones', value: 'actions', sortable: false })
      }

      return _headers
    },
    total () {
      return this.detail.total
    },
    totalToPay () {
      let total = this.detail.total

      if (['sent', 'confirmed'].includes(this.detail.status) && this.$is() === 'provider') {
        total = this.table.data.length
          ? this.table.data.reduce((acc, num) => {
            acc = acc + num.total
            return acc
          }, 0)
          : 0
      }

      return this.$is() === 'provider'
        ? total
        : total + this.detail.commission
    },
    subtotal () {
      return this.table.data.length ? this.table.data.reduce((acc, num) => {
        acc = acc + (num.amount * num.quantityConfirmed)
        return acc
      }, 0) : 0
    },
    iva () {
      return this.table.data.length ? this.table.data.reduce((acc, product) => {
        const iva = product.taxes.find(tax => tax.satCode === '002' && tax.base > 0)
        acc = acc + (iva ? (iva.base / 100) * (product.amount + this.ieps) * product.quantityConfirmed : 0)
        return acc
      }, 0) : 0
    },
    ieps () {
      return this.table.data.length ? this.table.data.reduce((acc, product) => {
        const iepsTaxes = product.taxes.filter(tax => tax.satCode === '003' && tax.base > 0)
        const hasBaseAmount = (iepsTaxes || []).every(tax => tax.baseAmount && tax.baseAmount >= 0)
        if (hasBaseAmount) {
          const taxesAmount = iepsTaxes.reduce((acc, tax) => {
            const base = tax.isQuota ? tax.base : (tax.base / 100)
            return acc + (tax.baseAmount * base)
          }, 0)
          return taxesAmount
        }
        const ieps = product.taxes.find(tax => tax.satCode === '003' && tax.base > 0)
        const amount = product.amount - (product.discount || 0)
        acc = acc + (ieps ? (ieps.base / 100) * amount * product.quantityConfirmed : 0)
        return acc
      }, 0) : 0
    },
    restaurant () {
      return this.$store.state.currentRestaurant.data
    },
    isCancelable () {
      return !['received', 'paid', 'canceled', 'pending_payment', 'invalid_invoice'].includes(this.detail.status) && this.purchaseOrderPermissions['change-purchase-order-status-to-cancelled']
    },
    provider () {
      return this.$store.state.currentProvider.data || ''
    },
    copyToLink () {
      return `${process.env.VUE_APP_API_URL}/v1/orders/${this.orderModel.$._id}/download`
    },
    purchaseOrderPermissions () {
      return this.$store.state.login.permissions['purchase-orders'].components['purchase-orders-list'].actions
    },
    isUserProvider () {
      return this.$store.state.userProvider
    },
    isValidInvoice () {
      const totalInvoice = this.detail.invoices.length ? this.detail.invoices.reduce((acc, invoice) => {
        acc = acc + invoice.total
        return acc
      }, 0) : 0
      return this.detail.total.toFixed(2) !== totalInvoice.toFixed(2)
    }
  },
  mounted () {
    window.addEventListener('keydown', this.keyDownHandler)
  },
  destroyed () {
    window.removeEventListener('keydown', this.keyDownHandler)
  },
  methods: {
    keyDownHandler (event) {
      if (event.code === 'Escape') this.$emit('close')
    },
    onCopy: function (e) {
      this.$toasted.show('Link copiado', { duration: 3000 })
    },
    getUnitName (unitId) {
      const unit = this.$store.state.login.units.data.find(unit => unit.id === unitId)
      return unit ? unit.name : ''
    },
    openSendByEmailDialog () {
      this.$refs.sendOrderByEmail.openDialog()
    },
    findIEPS (taxes) {
      if (taxes.length) {
        const ieps = taxes.find(tax => tax.satCode === '003' && tax.base > 0)
        return ieps
      }
      return null
    },
    hasTaxes (products) {
      return products.some(product => product.taxes.length)
    },
    searchCfdiUse (item, queryText, itemText) {
      const name = item.name.toLowerCase()
      const searchText = queryText.toLowerCase()
      return name.indexOf(searchText) >= 0
    },
    viewInvoice: async function (invoice) {
      await this.$refs.invoice.open(invoice)
    },
    viewCreditNote: async function (file) {
      this.file = file._id
      this.creditNoteViewerOpen = true
    },
    statusNames (status) {
      if (this.$is() === 'provider') {
        return {
          sent: 'Nueva orden',
          confirmed: 'Confirmada',
          canceled: 'Cancelada',
          invoiced: 'Facturada',
          invalid: 'Factura no coincide',
          received: 'Entregada',
          pending_payment: 'Cobrada',
          paid: 'Cobrada'
        }[status]
      }

      return {
        created: 'Sin enviar',
        sent: 'Enviada',
        confirmed: 'Confirmada',
        canceled: 'Cancelada',
        invoiced: 'Facturada',
        invalid: 'Factura no coincide',
        invalid_invoice: 'Factura no coincide',
        received: 'Recibida',
        pending_payment: 'Pendiente de pago',
        paid: 'Pagada'
      }[status]
    },
    statusColors (status) {
      return {
        created: 'grey',
        sent: 'grey',
        confirmed: 'info',
        canceled: 'red',
        invoiced: 'info',
        invalid: 'red',
        invalid_invoice: 'red',
        received: 'primary',
        pending_payment: 'primary',
        paid: 'primary'
      }[status]
    },
    closeDialog () {
      this.$emit('close')
    },
    updateTable: async function (data) {
      const { sortBy, sortDesc } = data
      if (this.order.id) {
        await this.load({
          limit: data.itemsPerPage,
          skip: (data.itemsPerPage * (data.page - 1)),
          sort: `${sortDesc[0] ? '-' : ''}${sortBy[0]}`
        })
      }
    },
    async load (params = null) {
      this.overlay = true
      params = params || {
        limit: this.table.options.itemsPerPage,
        skip: (this.table.options.itemsPerPage * (this.table.options.page - 1))
      }
      try {
        const { response } = await this.orderModel.detail({ params })
        const refundOrder = await this.refundOrder.detail({ params })
        this.table.loading = false
        this.refundedTable.loading = false
        response.data.products.map((element, index) => {
          element.number = index + 1
        })
        refundOrder.response.data.products.map((element, index) => {
          element.number = index + 1
        })
        this.detail = response.data
        this.refundDetail = refundOrder.response.data
        this.table.data = this.getProductsPricesWithTaxes(response.data.products)
        this.refundedTable.data = this.getProductsPricesWithTaxes(refundOrder.response.data.products)
        this.refundedTable.total = refundOrder.response.data.products.total
        this.table.total = response.data.products.length
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
      this.table.loading = false
    },
    getProductsPricesWithTaxes (products) {
      return ['provider'].includes(this.$is()) && ['sent', 'confirmed'].includes(this.detail.status)
        ? products.map(product => {
          let priceWithTaxes = product.amount

          if (product.taxes.length) {
            priceWithTaxes = this.calculatePriceProductWithTaxes(product)
          }

          const unitAmountWithTaxes = priceWithTaxes
          const iva = product.taxes.find(tax => tax.satCode === '002' && tax.base > 0)
          const ieps = this.findIEPS(product.taxes)
          const iepsAmount = ieps ? (ieps.isQuota ? ieps.base : ieps.base / 100) * (product.amount - (product.discount || 0)) : 0
          return Object.assign(
            {},
            product,
            {
              ieps: iepsAmount,
              iva: iva ? (iva.base / 100) * (product.amount + iepsAmount) : 0,
              unitAmountWithTaxes,
              total: unitAmountWithTaxes * product.quantityConfirmed
            }
          )
        })
        : products
    },
    calculatePriceProductWithTaxes (product) {
      let priceWithTaxes = product.amount
      const hasBaseAmount = (product.taxes || []).every(tax => tax.baseAmount && tax.baseAmount >= 0)

      if (hasBaseAmount) {
        const taxesAmount = product.taxes.reduce((acc, tax) => {
          const base = tax.isQuota ? tax.base : (tax.base / 100)
          return acc + (tax.baseAmount * base)
        }, 0)
        return priceWithTaxes + taxesAmount
      } else {
        let totalTaxes = 0
        const ieps = this.findIEPS(product.taxes)
        const iva = product.taxes.find(tax => tax.satCode === '002' && tax.base > 0)
        const otherTaxes = product.taxes
          .filter(tax => !['003', '002'].includes(tax.satCode))

        if (ieps) {
          const iepsAmount = (ieps.isQuota ? ieps.base : ieps.base / 100) * (product.amount - (product.discount || 0))
          priceWithTaxes = priceWithTaxes + iepsAmount
        }

        totalTaxes = otherTaxes.reduce((acc, num) => {
          let taxApplied
          if (num.isQuota) {
            taxApplied = num.base
          } else {
            taxApplied = (num.base / 100) * product.amount
          }
          acc += taxApplied
          return acc
        }, 0)

        if (iva) {
          priceWithTaxes = priceWithTaxes + (priceWithTaxes * (iva.base / 100))
        }

        priceWithTaxes = totalTaxes + priceWithTaxes

        return priceWithTaxes
      }
    },
    convertToDateString (date) {
      const d = new Date(date)
      return d.toLocaleDateString()
    },
    printOrder () {
      const printableJson = this.table.data
      print({
        printable: printableJson,
        properties: [
          { field: 'number', displayName: '' },
          { field: 'name', displayName: 'Producto' },
          { field: 'quantityRequested', displayName: 'Cantidad solicitada' },
          { field: 'amountWithTax', displayName: 'Precio unitario' },
          { field: 'totalWithTax', displayName: 'Total' }
        ],
        type: 'json',
        header: `<div>Orden: ${this.detail.uuid}</div><div>Total: $${this.total}</div>`
      })
    }
  }
}
</script>
